import { reactive, shallowReactive, toRaw } from 'vue'
import { AsyncState, CreateAsyncStateOptions } from './interfaces/AsyncState'
import { cloneInitData, ensureMeta, registerMeta } from './internal/registry'

/**
 * Cria um estado reativo de operação assíncrona.
 *
 * A forma e os valores iniciais são idênticos aos do `reactiveResponseDTO`.
 * O que muda é interno: o valor inicial é registrado para os resets e o estado
 * passa a ter uma sequência associada, usada por `fetchAsyncState` para
 * descartar respostas obsoletas.
 *
 * Função pura — não depende de escopo de `setup`, de `useAlert` nem de axios.
 * Serve em store, interceptor ou teste.
 */
export function createAsyncState<T>(
  initData: T,
  options: CreateAsyncStateOptions = {},
): AsyncState<T> {
  const factory = options.shallow ? shallowReactive : reactive

  const state = factory({
    data: cloneInitData(initData),
    loading: false,
    error: null,
    errorMsg: null,
    successfully: false,
    completed: false,
  }) as AsyncState<T>

  registerMeta(state, initData)

  return state
}

/**
 * Reseta o estado para os valores iniciais.
 *
 * Assinatura equivalente à do `resetReactiveResponseDTO`, incluindo o
 * parâmetro `loading` — útil para já entrar em skeleton sem piscar entre o
 * reset e o fetch seguinte.
 *
 * Diferença interna: o reset **invalida a requisição em voo**. Como
 * `ApiService` não aceita `signal`, ela continua rodando, mas a resposta que
 * chegar depois é descartada em vez de sobrescrever o estado limpo.
 */
export function resetAsyncState<T>(state: AsyncState<T>, initData: T, loading = false): void {
  ensureMeta(state).seq += 1

  state.data = cloneInitData(initData)
  state.loading = loading
  state.error = null
  state.errorMsg = null
  state.successfully = false
  state.completed = false
}

/**
 * Cria uma cópia independente do estado.
 *
 * Ao contrário do `cloneUnlinkedReactiveResponseDTO`, o `error` também é
 * copiado por valor — lá ele era atribuído por referência e continuava
 * compartilhado com o original, apesar do nome "unlinked".
 */
export function cloneAsyncState<T>(
  state: AsyncState<T>,
  options: CreateAsyncStateOptions = {},
): AsyncState<T> {
  const raw = toRaw(state)
  const clone = createAsyncState<T>(cloneInitData(raw.data), options)

  clone.data = cloneInitData(raw.data)
  clone.loading = raw.loading
  clone.error = raw.error ? raw.error.clone() : null
  clone.errorMsg = raw.errorMsg
  clone.successfully = raw.successfully
  clone.completed = raw.completed

  return clone
}
